<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();

const sources = useMapGetter('knowledgeSources/getSources');
const uiFlags = useMapGetter('knowledgeSources/getUIFlags');

const showForm = ref(false);
const form = reactive({ name: '', collection: 'default', content: '' });

const canSubmit = computed(
  () => form.name.trim() && form.content.trim() && !uiFlags.value.isCreating
);

onMounted(() => store.dispatch('knowledgeSources/get'));

const resetForm = () => {
  form.name = '';
  form.collection = 'default';
  form.content = '';
  showForm.value = false;
};

const createSource = async () => {
  if (!canSubmit.value) return;
  await store.dispatch('knowledgeSources/create', {
    name: form.name,
    collection: form.collection,
    source_type: 'document',
    content: form.content,
  });
  resetForm();
};

const ingest = id => store.dispatch('knowledgeSources/ingest', id);
const remove = id => store.dispatch('knowledgeSources/delete', id);
</script>

<template>
  <div class="knowledge-sources p-6">
    <header class="flex items-center justify-between mb-4">
      <div>
        <h1 class="text-xl font-medium">{{ t('KNOWLEDGE.SOURCES.HEADER') }}</h1>
        <p class="text-sm text-n-slate-11">{{ t('KNOWLEDGE.DESCRIPTION') }}</p>
      </div>
      <button class="button" data-testid="add-source" @click="showForm = !showForm">
        {{ t('KNOWLEDGE.SOURCES.ADD') }}
      </button>
    </header>

    <form v-if="showForm" class="mb-6 flex flex-col gap-2" @submit.prevent="createSource">
      <input v-model="form.name" :placeholder="t('KNOWLEDGE.SOURCES.FORM.NAME_PLACEHOLDER')" data-testid="source-name" />
      <input v-model="form.collection" :placeholder="t('KNOWLEDGE.SOURCES.FORM.COLLECTION_PLACEHOLDER')" data-testid="source-collection" />
      <textarea v-model="form.content" :placeholder="t('KNOWLEDGE.SOURCES.FORM.CONTENT_PLACEHOLDER')" data-testid="source-content" />
      <div class="flex gap-2">
        <button type="submit" class="button" :disabled="!canSubmit" data-testid="submit-source">
          {{ t('KNOWLEDGE.SOURCES.FORM.SUBMIT') }}
        </button>
        <button type="button" class="button clear" @click="resetForm">
          {{ t('KNOWLEDGE.SOURCES.FORM.CANCEL') }}
        </button>
      </div>
    </form>

    <p v-if="uiFlags.isFetching" data-testid="loading">{{ t('KNOWLEDGE.SOURCES.LOADING') }}</p>
    <p v-else-if="!sources.length" data-testid="empty">{{ t('KNOWLEDGE.SOURCES.EMPTY') }}</p>

    <table v-else class="w-full text-sm" data-testid="sources-table">
      <thead>
        <tr>
          <th class="text-left">{{ t('KNOWLEDGE.SOURCES.TABLE.NAME') }}</th>
          <th class="text-left">{{ t('KNOWLEDGE.SOURCES.TABLE.COLLECTION') }}</th>
          <th class="text-left">{{ t('KNOWLEDGE.SOURCES.TABLE.STATUS') }}</th>
          <th class="text-left">{{ t('KNOWLEDGE.SOURCES.TABLE.CHUNKS') }}</th>
          <th class="text-left">{{ t('KNOWLEDGE.SOURCES.TABLE.ACTIONS') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="source in sources" :key="source.id" data-testid="source-row">
          <td>{{ source.name }}</td>
          <td>{{ source.collection }}</td>
          <td>{{ source.status }}</td>
          <td>{{ source.chunk_count }}</td>
          <td class="flex gap-2">
            <button class="button small" @click="ingest(source.id)">
              {{ t('KNOWLEDGE.SOURCES.ACTIONS.INGEST') }}
            </button>
            <button class="button small alert" @click="remove(source.id)">
              {{ t('KNOWLEDGE.SOURCES.ACTIONS.DELETE') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
